/**
 * Board editor (Features #3/#4/#5 + Blocks 1/5/6).
 *
 * Block 1 (whitelabel): ships a RYFE-owned <MainMenu> (no local-file ops, no
 * social row) and a RYFE <WelcomeScreen> (logo from /api/branding), and disables
 * local-file actions via UIOptions.canvasActions — Autosave to the RYFE backend
 * is the only persistence model (Decision 2026-07-30). Image export stays (it's
 * an export, not a second save path).
 *
 * Block 6 (scene links): element links to /board/:id are intercepted and routed
 * in-app; a picker inserts button/preview links; a preview overlay renders the
 * target scene through the guarded content endpoint (no bypass).
 *
 * Block 5: an in-board panel inserts library elements into the open scene.
 */

import {
  Excalidraw,
  exportToBlob,
  MainMenu,
  WelcomeScreen,
} from "@excalidraw/excalidraw";
import { useCallback, useEffect, useRef, useState } from "react";

import type {
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";

import type {
  AppState,
  BinaryFiles,
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
} from "@excalidraw/excalidraw/types";

import type { OrderedExcalidrawElement as OrderedEl } from "@excalidraw/element/types";

import { ApiError, ryfeApi } from "../api";
import { parseScene, serializeScene } from "../board-data";
import { connectCollab } from "../collab";
import { mergeRemoteElements, throttle } from "../collab-sync";
import { createEncryptedShare } from "../share";
import { downloadBytes, jpegToPdf, safeFilename } from "../file-export";
import { backToFiles, navigate } from "../router";
import { openFileById } from "../open-file";
import { validateRyfeEmbeddable } from "../embed-allowlist";
import { renderRyfeEmbeddable } from "../embed-render";
import { useRyfe } from "../RyfeContext";
import { useDocumentTitle } from "../use-doc-title";
import { getDisplayMode } from "../frame-model";
import {
  insertSceneLink,
  legacyMainFrameIds,
  parseBoardLink,
  pickAutoMainFrameId,
  readPreviewLinks,
  resolveMainFrame,
} from "../scene-links";

import { ElementMenu } from "./ElementMenu";
import { FrameTools } from "./FrameTools";
import { LibraryInsertPanel } from "./LibraryInsertPanel";
import { ScenePreviewOverlay } from "./ScenePreviewOverlay";
import { ShareSubmenu } from "./ShareSubmenu";

import "./ryfe.scss";

import type { CollabConnection } from "../collab";
import type { PreviewLink } from "../scene-links";

const AUTOSAVE_MS = 1200;
const BROADCAST_MS = 150;

// Block 1: local-file operations are off; Autosave to S3 is the only persistence
// model. Image export stays (an export, not a save). Theme/background/clear stay.
const UI_OPTIONS = {
  canvasActions: {
    loadScene: false,
    saveToActiveFile: false,
    export: false as const,
    saveAsImage: true,
    toggleTheme: true,
    clearCanvas: true,
    changeViewBackgroundColor: true,
  },
};

// R4 (Punkt 1) — die Signatur MUSS den Anzeigemodus (D-54) enthalten: sie
// entscheidet, ob previewLinks (mit ihren Element-SNAPSHOTS) neu gesetzt
// werden. Ohne den Modus blieb nach dem ANZEIGE-Umschalten der alte Snapshot
// stehen — das Overlay las den alten customData-Stand und alle drei Modi
// verhielten sich wie der Default.
const previewSignature = (links: PreviewLink[]) =>
  links
    .map(
      (l) =>
        `${l.element.id}:${Math.round(l.element.x)}:${Math.round(
          l.element.y,
        )}:${Math.round(l.element.width)}:${Math.round(
          l.element.height,
        )}:${getDisplayMode(l.element)}`,
    )
    .join("|");

export const RyfeBoard = ({ fileId }: { fileId: string }) => {
  const { workspace, workspaces, branding } = useRyfe();
  // V1.2 (Block 5 enabler, also exercised by the 0.1 e2e): a board reached via
  // sharing may live in ANOTHER workspace than the active one. Resolve the
  // file across all of the user's workspaces on load; every subsequent call
  // (content, autosave, pointer PATCH, previews) uses the resolved workspace.
  // No new rights: each candidate lookup runs through the guarded routes.
  const [boardWs, setBoardWs] = useState<string | null>(null);
  const wsId = boardWs ?? workspace?.id;
  // CC_11 Block 2 (D-70) — the board's folder: "＋ Erstellen" creates the new
  // file in the SAME folder as this board and links it into the scene.
  const [boardFolder, setBoardFolder] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // P16 (Lane-C-Mock) — Öffnen-Fit pro Szene: "scene" (Default/Fallback) oder
  // "main" (Main-Frame füllt den Viewport). Persistenz pro Szene+Browser via
  // localStorage; ein serverseitiges Feld ist als Folgeschritt dokumentiert.
  const [openFit, setOpenFit] = useState<"scene" | "main">(() => {
    try {
      return window.localStorage.getItem(`ryfe-openfit-${fileId}`) === "main"
        ? "main"
        : "scene";
    } catch {
      return "scene";
    }
  });
  const [openFitOpen, setOpenFitOpen] = useState(false);
  const [name, setName] = useState("Board");
  // R4 — Browser-Tab trägt den Dateinamen.
  useDocumentTitle(name);
  const [initialData, setInitialData] =
    useState<ExcalidrawInitialDataState | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [saveState, setSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  // v1.3.0 Block 1 — the reason a save failed, shown until the next success.
  const [saveError, setSaveError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [previewLinks, setPreviewLinks] = useState<PreviewLink[]>([]);
  const [viewVersion, setViewVersion] = useState(0);
  // Block B/C: the scene → main-frame pointer lives on the file meta, not in the
  // scene. `sceneTick` re-renders the frame tools when selection/frames change.
  const [mainFrameId, setMainFrameId] = useState<string | null>(null);
  // Block 9 — boards embedding THIS scene (reverse index; drives statements).
  const [embeddedIn, setEmbeddedIn] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [sceneTick, setSceneTick] = useState(0);
  // CC_05 Block 6 — the single currently-selected element (Element-Menü).
  const [selectedEl, setSelectedEl] = useState<OrderedExcalidrawElement | null>(
    null,
  );
  const apiRef = useRef<ExcalidrawImperativeAPI | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewSig = useRef("");
  const hasPreviews = useRef(false);
  const mainFrameRef = useRef<string | null>(null);
  const frameSig = useRef("");

  const collabRef = useRef<CollabConnection | null>(null);
  const applyingRemote = useRef(false);
  const broadcastRef = useRef<
    ((els: readonly OrderedExcalidrawElement[]) => void) | null
  >(null);

  // Load scene + name.
  useEffect(() => {
    let cancelled = false;
    const activeWs = workspace?.id;
    if (!activeWs) {
      return;
    }
    setStatus("loading");
    (async () => {
      let metaMainFrame: string | null = null;
      // Resolve the workspace the file actually lives in: active first, then
      // the user's other memberships (cross-workspace shares, Block 5).
      let wsId = activeWs;
      const candidates = [
        activeWs,
        ...workspaces.map((w) => w.id).filter((id) => id !== activeWs),
      ];
      // Cross-tenant shares (Block 5): the owning workspace may not be among
      // the user's memberships at all — the shared-with-me listing names it.
      try {
        const { items } = await ryfeApi.sharedWithMe();
        for (const it of items) {
          if (!candidates.includes(it.workspaceId)) {
            candidates.push(it.workspaceId);
          }
        }
      } catch {
        /* listing unavailable — membership candidates still apply */
      }
      for (const cand of candidates) {
        try {
          const f = await ryfeApi.getFile(cand, fileId);
          wsId = cand;
          if (!cancelled) {
            setName(f.name);
            setBoardWs(cand);
            setBoardFolder((f as { folder_id?: string }).folder_id ?? null);
            setEmbeddedIn(f.embedded_in || []);
          }
          metaMainFrame = f.main_frame_id ?? null;
          break;
        } catch {
          /* try the next workspace; content load below decides the outcome */
        }
      }
      try {
        const buf = await ryfeApi.getFileContent(wsId, fileId);
        const text = new TextDecoder().decode(new Uint8Array(buf));
        const scene = parseScene(text);
        if (cancelled) {
          return;
        }
        // CC_11-Revision — beim Öffnen die Szene einpassen: der gespeicherte
        // appState trägt Scroll/Zoom der LETZTEN Sitzung, was auf anderen
        // Bildschirmen regelmäßig „irgendwo im Nichts" landet. Excalidraws
        // eigenes `scrollToContent`-Initial-Flag holt den Inhalt in den
        // Viewport (D-04: öffentliches initialData-API, kein Fork-Eingriff).
        setInitialData({ ...scene, scrollToContent: true });
        setStatus("ready");
        // Block B.4 — one-time migration of the OLD in-scene "main" marking to
        // the pointer, only if the file has no pointer yet. Several markings →
        // first (scene order ≈ creation order) wins; recorded for the report.
        const els = (scene.elements || []) as unknown as OrderedEl[];
        const legacy = legacyMainFrameIds(els);
        let effective = metaMainFrame;
        if (!effective && legacy.length > 0) {
          effective = legacy[0];
          if (legacy.length > 1) {
            // eslint-disable-next-line no-console
            console.warn(
              `RYFE: scene had ${legacy.length} legacy main-frame markings; ` +
                `kept the first (${legacy[0]}).`,
            );
          }
          persistMainFrame(effective);
        }
        mainFrameRef.current = effective;
        setMainFrameId(effective);
      } catch {
        if (!cancelled) {
          setInitialData({ elements: [], appState: {}, files: {} });
          setStatus("error");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // persistMainFrame is stable (its own deps are wsId/fileId).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id, workspaces, fileId]);

  // Collab relay.
  useEffect(() => {
    if (status !== "ready" || !fileId) {
      return;
    }
    const applyRemote = (payload: unknown) => {
      const api = apiRef.current;
      if (!api || !Array.isArray(payload)) {
        return;
      }
      const local = api.getSceneElementsIncludingDeleted();
      const merged = mergeRemoteElements(
        local,
        payload as OrderedExcalidrawElement[],
        api.getAppState(),
      );
      applyingRemote.current = true;
      api.updateScene({ elements: merged });
    };

    const conn = connectCollab(fileId, {
      onUpdate: (payload) => applyRemote(payload),
      onPeerJoined: () => {
        const api = apiRef.current;
        if (api && conn.canWrite) {
          conn.sendUpdate(api.getSceneElementsIncludingDeleted());
        }
      },
    });
    collabRef.current = conn;
    broadcastRef.current = throttle(
      (els: readonly OrderedExcalidrawElement[]) => conn.sendUpdate(els),
      BROADCAST_MS,
    );

    return () => {
      conn.close();
      collabRef.current = null;
      broadcastRef.current = null;
    };
  }, [status, fileId]);

  const save = useCallback(
    async (
      elements: readonly OrderedExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
    ) => {
      if (!wsId) {
        return;
      }
      setSaveState("saving");
      try {
        const blob = serializeScene(elements, appState, files);
        // CC_05 Block 1 — assert 'board' so the server refuses this write if the
        // file is not a board (e.g. a mis-routed md note). Defense-in-depth: a
        // rejection surfaces as a visible save error, never a silent overwrite.
        await ryfeApi.putFileContent(
          wsId,
          fileId,
          blob,
          "application/json",
          "board",
        );
        setSaveError(null);
        setSaveState("saved");
      } catch (err) {
        // v1.3.0 Block 1 (D-21): a failed save must NEVER be silent. Until now
        // the catch fell back to "idle", which renders an empty string — the
        // bar simply went blank and the user assumed the board was stored. The
        // classic production case is a reverse proxy whose client_max_body_size
        // is below the backend limit: every save past ~1 MB is rejected with
        // 413 and the scene is lost on reload, with nothing on screen saying so.
        setSaveError(
          err instanceof ApiError && err.status === 413
            ? "Board zu groß für den Server-Upload (413). Der Reverse-Proxy muss client_max_body_size mindestens auf das Backend-Limit setzen — siehe docs/DEPLOY-v1.3.md."
            : err instanceof ApiError
            ? `Speichern fehlgeschlagen (${err.status}): ${err.message}`
            : "Speichern fehlgeschlagen — keine Verbindung zum Server.",
        );
        setSaveState("error");
      }
    },
    [wsId, fileId],
  );

  // Block B — persist the main-frame pointer (optimistic + PATCH). `null` clears.
  const persistMainFrame = useCallback(
    async (id: string | null) => {
      mainFrameRef.current = id;
      setMainFrameId(id);
      if (!wsId) {
        return;
      }
      try {
        await ryfeApi.patchFile(wsId, fileId, { mainFrameId: id });
      } catch {
        /* keep optimistic value; a later save/reload reconciles */
      }
    },
    [wsId, fileId],
  );

  const refreshPreviewLinks = useCallback(
    (elements: readonly OrderedExcalidrawElement[]) => {
      const links = readPreviewLinks(elements);
      const sig = previewSignature(links);
      if (sig !== previewSig.current) {
        previewSig.current = sig;
        hasPreviews.current = links.length > 0;
        setPreviewLinks(links);
      }
    },
    [],
  );

  // Block B/C — react to frame/selection changes: re-render the tools when the
  // selected frame or the frame set changes, and auto-assign the main anchor
  // (first sectional) while the pointer is still empty.
  const refreshFrames = useCallback(
    (elements: readonly OrderedExcalidrawElement[], selectedSig: string) => {
      const frames = elements.filter(
        (el) =>
          !el.isDeleted && (el.type === "frame" || el.type === "magicframe"),
      );
      const sig = `${selectedSig}|${frames.map((f) => f.id).join(",")}|${
        mainFrameRef.current ?? ""
      }`;
      if (sig !== frameSig.current) {
        frameSig.current = sig;
        setSceneTick((t) => t + 1);
      }
      if (!mainFrameRef.current) {
        const auto = pickAutoMainFrameId(elements, mainFrameRef.current);
        if (auto) {
          persistMainFrame(auto);
        }
      }
    },
    [persistMainFrame],
  );

  // Reposition the preview overlay on pan/zoom (only when previews exist, so a
  // plain board never re-renders on scroll). Stable identity — passing a fresh
  // closure to <Excalidraw> each render would feed an update loop.
  const onScrollChange = useCallback(() => {
    if (hasPreviews.current) {
      setViewVersion((v) => v + 1);
    }
  }, []);

  const handleExcalidrawAPI = useCallback(
    (api: ExcalidrawImperativeAPI | null) => {
      apiRef.current = api;
      setApiReady(!!api);
      const w = window as Window & {
        __RYFE_EXPOSE_API__?: boolean;
        __RYFE_EXCALIDRAW_API__?: ExcalidrawImperativeAPI;
      };
      if (api && w.__RYFE_EXPOSE_API__) {
        w.__RYFE_EXCALIDRAW_API__ = api;
      }
    },
    [],
  );

  const onChange = useCallback(
    (
      elements: readonly OrderedExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
    ) => {
      if (status !== "ready") {
        return;
      }
      refreshPreviewLinks(elements);
      const selectedIds = Object.keys(appState.selectedElementIds || {});
      const selectedSig = selectedIds.slice().sort().join(",");
      refreshFrames(elements, selectedSig);
      // CC_05 Block 6 — track the single selected element for the Element-Menü.
      const single =
        selectedIds.length === 1
          ? elements.find((el) => el.id === selectedIds[0] && !el.isDeleted) ||
            null
          : null;
      setSelectedEl(single);
      if (applyingRemote.current) {
        applyingRemote.current = false;
        return;
      }
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(
        () => save(elements, appState, files),
        AUTOSAVE_MS,
      );
      if (collabRef.current?.canWrite) {
        broadcastRef.current?.(elements);
      }
    },
    [save, status, refreshPreviewLinks, refreshFrames],
  );

  const onLinkOpen = useCallback(
    (
      element: NonDeletedExcalidrawElement,
      event: CustomEvent<{ nativeEvent: MouseEvent | React.PointerEvent }>,
    ) => {
      const target = parseBoardLink(element.link);
      if (!target) {
        // Not an in-app link (external URL etc.) — let Excalidraw handle it.
        return;
      }
      // CC_05 Block 1 (D-29) — route by the TARGET's kind, not blindly to the
      // board editor. A link onto a kind='md' note must open the MD editor; the
      // old code always opened /board/:id, and the board autosave then
      // overwrote the note. openFileById fetches the target kind first.
      event.preventDefault();
      const candidates = [wsId, ...workspaces.map((w) => w.id)].filter(
        (id): id is string => !!id,
      );
      void openFileById(candidates, target);
    },
    [wsId, workspaces],
  );

  // CC_09 P10 — Board-Export JPG/PDF über den öffentlichen Host-Export
  // (exportToBlob → JPEG; PDF bettet das JPEG ein). PNG/SVG/Excalidraw bleiben
  // im nativen Excalidraw-Menü.
  const exportBoard = async (kind: "jpg" | "pdf") => {
    const api = apiRef.current;
    if (!api) {
      return;
    }
    const blob = await exportToBlob({
      elements: api.getSceneElements(),
      appState: { ...api.getAppState(), exportBackground: true },
      files: api.getFiles(),
      mimeType: "image/jpeg",
      quality: 0.92,
    });
    const jpeg = new Uint8Array(await blob.arrayBuffer());
    if (kind === "jpg") {
      downloadBytes(safeFilename(name, "jpg"), "image/jpeg", jpeg);
    } else {
      downloadBytes(
        safeFilename(name, "pdf"),
        "application/pdf",
        jpegToPdf(jpeg),
      );
    }
  };

  // CC_11 Block 2 (D-70) — "＋ Erstellen": create a file of the chosen kind in
  // the SAME folder as this board and insert a scene-link button to it. Same
  // body seeds as the files page (RyfeFiles.createEntry); route-by-kind on
  // click stays intact because insertSceneLink stores the kind-agnostic path.
  const createAndLink = async (
    kind: "list" | "board" | "md",
    label: string,
  ) => {
    setCreateOpen(false);
    const api = apiRef.current;
    if (!api || !wsId || !boardFolder) {
      return;
    }
    const newName = window.prompt(`${label} benennen`, `Neue ${label}`);
    if (!newName || !newName.trim()) {
      return;
    }
    try {
      const body =
        kind === "md"
          ? `# ${newName.trim()}\n\n`
          : kind === "board"
          ? serializeScene([], {}, {})
          : "";
      const contentType = kind === "md" ? "text/markdown" : "application/json";
      const file = await ryfeApi.createFile(
        wsId,
        boardFolder,
        newName.trim(),
        kind,
        body,
        contentType,
      );
      insertSceneLink(api, {
        targetFileId: file.id,
        targetName: file.name,
        mode: "button",
      });
    } catch {
      /* creation failed — nothing was inserted */
    }
  };

  // Redesign Welle R — „Ordner" im Erstellen-Menü: legt einen Ordner im selben
  // Ordner wie das Board an (kein Szenen-Link, anders als createAndLink).
  const createFolderHere = async () => {
    setCreateOpen(false);
    if (!wsId || !boardFolder) {
      return;
    }
    const newName = window.prompt("Ordner benennen", "Neuer Ordner");
    if (!newName || !newName.trim()) {
      return;
    }
    try {
      await ryfeApi.createFolder(wsId, boardFolder, newName.trim());
    } catch {
      /* creation failed — nothing was created */
    }
  };

  // P16 (Lane-C-Mock) — den gewählten Bereich in den Viewport holen: "main"
  // passt den Main-Frame ein (Zoom+Scroll aus Frame-Box und Viewport-Maßen
  // berechnet), "scene" die Bounding-Box aller Elemente (Excalidraws
  // scrollToContent-Initial-Fit). Fallback bei fehlendem Main-Frame = "scene".
  const applyOpenFit = useCallback((mode: "scene" | "main") => {
    const api = apiRef.current;
    if (!api) {
      return;
    }
    const st = api.getAppState();
    const els = api.getSceneElements();
    if (els.length === 0) {
      return;
    }
    let box: { x: number; y: number; width: number; height: number } | null =
      null;
    if (mode === "main") {
      const frame = resolveMainFrame(els, mainFrameRef.current);
      if (frame) {
        box = {
          x: frame.x,
          y: frame.y,
          width: frame.width,
          height: frame.height,
        };
      }
    }
    if (!box) {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const el of els) {
        minX = Math.min(minX, el.x);
        minY = Math.min(minY, el.y);
        maxX = Math.max(maxX, el.x + el.width);
        maxY = Math.max(maxY, el.y + el.height);
      }
      box = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
    const vw = st.width || 800;
    const vh = st.height || 600;
    const zoom = Math.min(
      10,
      Math.max(
        0.1,
        Math.min(vw / Math.max(1, box.width), vh / Math.max(1, box.height)) *
          0.9,
      ),
    );
    api.updateScene({
      appState: {
        scrollX: (vw / zoom - box.width) / 2 - box.x,
        scrollY: (vh / zoom - box.height) / 2 - box.y,
        zoom: { value: zoom as never },
      },
    });
  }, []);

  // Beim Öffnen den gewählten Fit IMMER aktiv anwenden. R4 (Punkt 16):
  // vorher lief der Effekt nur für "main" und verließ sich bei "scene" auf
  // Excalidraws scrollToContent — das scrollt aber nur und ZOOMT nicht, der
  // gespeicherte Zoom (oft 100 %) blieb stehen.
  const fitAppliedRef = useRef(false);
  useEffect(() => {
    fitAppliedRef.current = false;
  }, [fileId]);
  useEffect(() => {
    if (!apiReady || status !== "ready" || fitAppliedRef.current) {
      return;
    }
    fitAppliedRef.current = true;
    // Ein Frame später, damit Excalidraw die Viewport-Maße gesetzt hat.
    const mode = openFit;
    requestAnimationFrame(() => applyOpenFit(mode));
  }, [apiReady, status, openFit, applyOpenFit]);

  const chooseOpenFit = (mode: "scene" | "main") => {
    setOpenFit(mode);
    setOpenFitOpen(false);
    try {
      window.localStorage.setItem(`ryfe-openfit-${fileId}`, mode);
    } catch {
      /* Persistenz optional */
    }
    applyOpenFit(mode);
  };

  // CC_11-Revision — das Board nutzt jetzt dasselbe EINE Teilen-Submenü wie
  // Liste und Notiz (CC_09 B7): Mail-Invite + Snapshot-Link in einem Dialog.
  // Der Snapshot-Erzeuger ist der bisherige clientseitig verschlüsselte
  // Board-Share (D-57: Board wie bisher).
  const createBoardSnapshot = async (): Promise<string> => {
    const api = apiRef.current;
    if (!api || !wsId) {
      throw new Error("board not ready");
    }
    return createEncryptedShare({
      workspaceId: wsId,
      fileId,
      elements: api.getSceneElements(),
      appState: api.getAppState(),
      files: api.getFiles(),
    });
  };

  return (
    <div className="ryfe-board">
      <div className="ryfe-board__bar">
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={() => navigate(backToFiles(boardFolder))}
          title="Zurück in den Ordner der Datei"
        >
          ← Dateien
        </button>
        <span className="ryfe-board__bar__title">{name}</span>
        {/* CC_07 Block 5 — Umbenennen im Detailview (Board). */}
        <button
          className="ryfe-btn ryfe-btn--ghost"
          title="Board umbenennen"
          onClick={async () => {
            if (!wsId) {
              return;
            }
            const next = window.prompt("Board umbenennen", name);
            if (!next || next === name) {
              return;
            }
            try {
              await ryfeApi.patchFile(wsId, fileId, { name: next });
              setName(next);
            } catch {
              /* ignore — the board scene is unaffected */
            }
          }}
        >
          ✎
        </button>
        <span
          className={saveState === "error" ? "ryfe-save-error" : "ryfe-muted"}
          data-testid="board-save-state"
          title={saveError || undefined}
        >
          {saveState === "saving"
            ? "Speichert…"
            : saveState === "saved"
            ? "Gespeichert"
            : saveState === "error"
            ? "⚠ Nicht gespeichert"
            : ""}
        </span>
        <button
          className="ryfe-btn ryfe-btn--ghost"
          onClick={() => setLibraryOpen((o) => !o)}
        >
          Bibliothek
        </button>
        {/* CC_11 Block 2 (D-70) — Menü-Button: neue Datei im SELBEN Ordner
            anlegen + als Szenen-Link einfügen. Redesign Welle R: der frühere
            eigenständige „Verknüpfen"-Button (SceneLinkPicker) entfällt —
            Verknüpfungen laufen über Frames (ElementMenu). Bestehende Szenen-
            Links werden weiter gerendert. */}
        <div className="ryfe-elmenu__wrap">
          <button
            className="ryfe-btn ryfe-btn--ghost"
            data-testid="board-create-menu"
            onClick={() => setCreateOpen((o) => !o)}
          >
            ＋ Erstellen ▾
          </button>
          {createOpen && (
            <div className="ryfe-elmenu__sub" role="menu">
              {(
                [
                  { kind: "list", label: "Tabelle" },
                  { kind: "board", label: "Whiteboard" },
                  { kind: "md", label: "Notiz" },
                ] as const
              ).map((o) => (
                <button
                  key={o.kind}
                  className="ryfe-elmenu__item"
                  onClick={() => void createAndLink(o.kind, o.label)}
                >
                  {o.label}
                </button>
              ))}
              {/* Redesign Welle R — „Ordner" anlegen (im selben Ordner; kein
                  Szenen-Link, da ein Ordner kein Szenen-Ziel ist). */}
              <button
                className="ryfe-elmenu__item"
                data-testid="board-create-folder"
                onClick={() => void createFolderHere()}
              >
                Ordner
              </button>
            </div>
          )}
        </div>
        {/* P16 (Lane-C-Mock) — Öffnen-Fit: Gesamte Szene | Main-Frame,
            pro Szene gemerkt. */}
        <div className="ryfe-elmenu__wrap">
          <button
            className="ryfe-btn ryfe-btn--ghost"
            data-testid="board-openfit"
            title="Skalierung beim Öffnen"
            onClick={() => setOpenFitOpen((o) => !o)}
          >
            Öffnen: {openFit === "main" ? "Main-Frame" : "Gesamte Szene"} ▾
          </button>
          {openFitOpen && (
            <div className="ryfe-elmenu__sub" role="menu">
              <button
                className="ryfe-elmenu__item"
                onClick={() => chooseOpenFit("scene")}
              >
                Gesamte Szene
                {openFit === "scene" && (
                  <span className="ryfe-elmenu__chk">✓</span>
                )}
              </button>
              <button
                className="ryfe-elmenu__item"
                onClick={() => chooseOpenFit("main")}
              >
                Main-Frame
                {openFit === "main" && (
                  <span className="ryfe-elmenu__chk">✓</span>
                )}
              </button>
            </div>
          )}
        </div>
        <button
          className="ryfe-btn ryfe-btn--ghost"
          title="Board als JPG herunterladen"
          onClick={() => void exportBoard("jpg")}
        >
          ⬇ JPG
        </button>
        <button
          className="ryfe-btn ryfe-btn--ghost"
          title="Board als PDF herunterladen"
          onClick={() => void exportBoard("pdf")}
        >
          ⬇ PDF
        </button>
        <button
          className="ryfe-btn ryfe-btn--primary"
          onClick={() => setShareOpen(true)}
        >
          Teilen
        </button>
      </div>
      {/* v1.3.0 Block 1 — a failed save stays on screen with its reason. It is
          NOT dismissible on purpose: the scene only lives in the browser until
          a save succeeds, so the warning must survive until it does. */}
      {saveError && (
        <div className="ryfe-save-banner" role="alert">
          <span>{saveError}</span>
          <button
            className="ryfe-btn ryfe-btn--ghost"
            onClick={() => {
              const api = apiRef.current;
              if (api) {
                void save(
                  api.getSceneElementsIncludingDeleted(),
                  api.getAppState(),
                  api.getFiles(),
                );
              }
            }}
          >
            Erneut speichern
          </button>
        </div>
      )}
      {shareOpen && (
        <ShareSubmenu
          target={{ kind: "file", id: fileId, name }}
          wsId={wsId ?? undefined}
          onClose={() => setShareOpen(false)}
          onInviteByEmail={
            wsId
              ? async (email, role) => {
                  await ryfeApi.inviteToFile(
                    wsId,
                    fileId,
                    email,
                    role === "editor" ? "editor" : "viewer",
                  );
                }
              : undefined
          }
          onCreateSnapshot={wsId ? createBoardSnapshot : undefined}
        />
      )}
      <div className="ryfe-board__editor">
        {status === "loading" ? (
          <div className="ryfe-muted">Board lädt…</div>
        ) : (
          <>
            <Excalidraw
              initialData={initialData}
              onChange={onChange}
              onScrollChange={onScrollChange}
              onLinkOpen={onLinkOpen}
              UIOptions={UI_OPTIONS}
              validateEmbeddable={validateRyfeEmbeddable}
              renderEmbeddable={renderRyfeEmbeddable}
              onExcalidrawAPI={handleExcalidrawAPI}
            >
              <MainMenu>
                <MainMenu.DefaultItems.SaveAsImage />
                <MainMenu.DefaultItems.ChangeCanvasBackground />
                <MainMenu.Separator />
                <MainMenu.DefaultItems.ToggleTheme allowSystemTheme={false} />
                <MainMenu.DefaultItems.ClearCanvas />
                <MainMenu.DefaultItems.Help />
              </MainMenu>
              <WelcomeScreen>
                <WelcomeScreen.Center>
                  <WelcomeScreen.Center.Logo>
                    {branding.logo ? (
                      <img
                        src={branding.logo}
                        alt={branding.productName}
                        style={{ maxHeight: 60 }}
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display =
                            "none";
                        }}
                      />
                    ) : (
                      branding.productName
                    )}
                  </WelcomeScreen.Center.Logo>
                  <WelcomeScreen.Center.Heading>
                    {branding.productName}
                  </WelcomeScreen.Center.Heading>
                  <WelcomeScreen.Hints.ToolbarHint />
                  <WelcomeScreen.Hints.MenuHint />
                </WelcomeScreen.Center>
              </WelcomeScreen>
            </Excalidraw>
            {apiReady && apiRef.current && wsId && previewLinks.length > 0 && (
              <ScenePreviewOverlay
                wsId={wsId}
                api={apiRef.current}
                links={previewLinks}
                version={viewVersion}
              />
            )}
            {apiReady && apiRef.current && wsId && (
              <FrameTools
                api={apiRef.current}
                mainFrameId={mainFrameId}
                setMainFrame={persistMainFrame}
                viewVersion={viewVersion}
                sceneTick={sceneTick}
                embeddedIn={embeddedIn}
              />
            )}
            {libraryOpen && apiReady && apiRef.current && wsId && (
              <LibraryInsertPanel
                wsId={wsId}
                api={apiRef.current}
                onClose={() => setLibraryOpen(false)}
              />
            )}
            {/* CC_05 Block 6 + CC_06 Block 5 — ein Element-Menü für JEDE
                Einzelauswahl (Shape/Text UND Frame). Die frame-spezifischen
                Aktionen (Bild, Größe, Vorschau-Anker, Verknüpfen, Umbenennen)
                liegen jetzt hier; FrameTools trägt nur noch die passiven
                Hinweise (Anker-Badge, Resize-/Lösch-Toast). */}
            {apiReady && apiRef.current && wsId && selectedEl && (
              <ElementMenu
                wsId={wsId}
                api={apiRef.current}
                currentFileId={fileId}
                selected={selectedEl}
                wsCandidates={[wsId, ...workspaces.map((w) => w.id)].filter(
                  (id): id is string => !!id,
                )}
                mainFrameId={mainFrameId}
                setMainFrame={persistMainFrame}
                uploadStamp={viewVersion}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
